import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';

import { apiPaths } from '../../../../../service/apiPaths';
import ApiService from '../../../../../service/ApiService';
import { IAddClient, IClient } from '../../Client.props';

const useEditClient = (clientId: string, setMethod: () => void) => {
  const [formData, setFormData] = useState<IAddClient>({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    description: "",
    linkedin: "",
    interest: "",
    outputStatus: "",
    services: "",
    platform: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [client, setClient] = useState<IClient | null>(null);
  const [interest, setInterest] = useState<any[]>([]);
  const [interestId, setInterestId] = useState<string>("");
  const [services, setServices] = useState<any[]>([]);
  const [serviceId, setServiceId] = useState<string>("");
  const [outputStatuses, setOutputStatuses] = useState<any[]>([]);
  const [outputStatusId, setOutputStatusId] = useState<string>("");
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [platformId, setPlatformId] = useState<string>("");
  useEffect(() => {
    fetchData();
    fetchInterestsData();
    fetchServicesData();
    fetchOutputStatusesData();
    fetchPlatformsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getAllClients}`,
      });
      const data = response.data.find(
        (client: { _id: any }) => client?._id === clientId || null
      );
      setClient(data);
      populateFormData(data);
    } catch (error) {
      toast.error(`Error - ${error}`, {
        duration: 5000,
      });
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchInterestsData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.interests}`,
      });
      setInterest(result);
    } catch (error) {
      console.error("Error fetching interests:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServicesData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.services}`,
      });
      setServices(result);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutputStatusesData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.outputStatus}`,
      });
      setOutputStatuses(result);
    } catch (error) {
      console.error("Error fetching output statuses:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformsData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.platforms}`,
      });
      setPlatforms(result);
    } catch (error) {
      console.error("Error fetching platforms:", error);
    } finally {
      setLoading(false);
    }
  };
  const populateFormData = (clientData: IClient) => {
    setFormData({
      name: clientData.name,
      email: clientData.email || "",
      phone: clientData.phone || "",
      companyName: clientData.companyName || "",
      description: clientData.description || "",
      linkedin: clientData.linkedin || "",
      interest: clientData.interest?._id,
      outputStatus: clientData.outputStatus?._id,
      services: clientData.services?._id,
      platform: clientData.platform?._id,
    });
    setInterestId(clientData.interest?._id || "");
    setOutputStatusId(clientData.outputStatus?._id || "");
    setServiceId(clientData.services?._id || "");
    setPlatformId(clientData.platform?._id || "");
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleSubmit: SubmitHandler<IAddClient> = async () => {
    try {
      const response = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateClient}${clientId}`,
        data: {
          ...formData,
          interest: interestId,
          outputStatus: outputStatusId,
          services: serviceId,
          platform: platformId,
        },
      });
      if (response !== "undefined") {
        toast.success("Client updated successfully", {
          duration: 5000,
        });
        setTimeout(() => {
          setMethod();
        }, 1500);
      }
    } catch (error) {
      toast.error(`Error - ${error}`, {
        duration: 5000,
      });
      console.error("Error updating client:", error);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    formData,
    client,
    handleChange,
    handleSubmit,
    setInterestId,
    interest,
    interestId,
    services,
    serviceId,
    setServiceId,
    outputStatuses,
    outputStatusId,
    setOutputStatusId,
    platforms,
    platformId,
    setPlatformId,
  };
};
export default useEditClient;
